
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clock, CheckCircle, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface FreeTrialDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FreeTrialDialog = ({ isOpen, onClose }: FreeTrialDialogProps) => {
  const [email, setEmail] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleStartTrial = async () => {
    try {
      setIsStarting(true);
      
      // Check if user is already authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // User is logged in, create trial directly
        console.log('Creating trial for authenticated user:', session.user.id);
        
        // Check if user already has a trial
        const { data: existingTrial } = await supabase
          .from('free_trials')
          .select('*')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (existingTrial) {
          if (existingTrial.status === 'active') {
            toast({
              title: "Trial Already Active",
              description: "You already have an active free trial.",
              className: "bg-blue-50 border-blue-200",
            });
            onClose();
            navigate('/dashboard');
            return;
          } else {
            toast({
              title: "Trial Already Used",
              description: "You have already used your free trial. Please upgrade to continue.",
              variant: "destructive",
            });
            onClose();
            navigate('/plan-selection');
            return;
          }
        }

        // Create new trial for authenticated user
        const { data: newTrial, error: trialError } = await supabase
          .from('free_trials')
          .insert({
            user_id: session.user.id,
            email: session.user.email || email,
          })
          .select()
          .single();

        if (trialError) throw trialError;

        toast({
          title: "Free Trial Started!",
          description: "You now have 24 hours of full access to CSO Agent.",
          className: "bg-green-50 border-green-200",
        });
        
        onClose();
        navigate('/dashboard');
      } else {
        // User not logged in, redirect to signup with trial intent
        onClose();
        navigate('/auth?mode=signup&trial=true');
      }
    } catch (error: any) {
      console.error('Failed to start trial:', error);
      toast({
        title: "Error Starting Trial",
        description: error.message || "Failed to start trial. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsStarting(false);
    }
  };

  const trialFeatures = [
    'Unlimited document generation',
    'Advanced strategy chat',
    'All document templates',
    'Business assessment tools',
    'Analytics dashboard',
    'AI-powered insights',
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            Start Your Free Trial
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="text-center">
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-blue-800">24 Hours Full Access</h3>
              <p className="text-sm text-blue-600">
                Experience all premium features with no limitations
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">What's included:</h4>
            <div className="space-y-2">
              {trialFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-gray-600">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isStarting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStartTrial}
                disabled={isStarting}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {isStarting ? 'Starting...' : 'Start Free Trial'}
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            No credit card required. Trial automatically expires after 24 hours.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
